/* EXC text signup (sections/exc-sms-offer.liquid, in the Pop-ups group).
   Shows the tab and, on first tap, builds the popup from its <template>:
   screen 1 asks what they are shopping for, screen 2 takes the number and
   subscribes it to the Klaviyo list with SMS consent (the answer is saved on
   the profile as "Shopping for"), screen 3 shows the code and adds it to the
   cart. Once someone has signed up on this browser the tab stays hidden.

   Performance: the tab's position and visibility follow three things on the
   page (the product page sticky add to cart bar, Shopify's cookie banner, the
   cart drawer). They are watched here with MutationObservers on just those
   elements and mirrored onto the tab's own wrapper as data attributes. CSS
   :has() rules on <body> or <html> did the same job but made the browser
   re-check them on every change anywhere on the page: measured on a throttled
   phone, about 4.5s of extra style work per product page load. */

// 2024-10-15 rejected the SMS consent field (and retires 2026-10-15); tested 202 on 2026-07-15
const KLAVIYO_REVISION = '2026-07-15';
const DONE_KEY = 'excSmsSignedUp';

const store = {
  get(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  },
  set(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* private mode */ }
  },
};

// US and Canada numbers: 10 digits, or 11 starting with 1. Returns +1XXXXXXXXXX.
function toE164(raw) {
  let digits = String(raw || '').replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') digits = digits.slice(1);
  return digits.length === 10 ? `+1${digits}` : null;
}

// Mirror a condition on another element onto the wrapper as data-<name>
function mirror(root, name, element, attributes, test) {
  const sync = () => root.toggleAttribute(`data-${name}`, test(element));
  sync();
  new MutationObserver(sync).observe(element, { attributes: true, attributeFilter: attributes });
}

function watchPage(root) {
  const bar = document.querySelector('.exc-sticky-atc');
  if (bar) mirror(root, 'bar', bar, ['data-visible'], (el) => el.dataset.visible === 'true');

  const cart = document.getElementById('modal-cart');
  if (cart) mirror(root, 'cart-open', cart, ['aria-hidden'], (el) => el.getAttribute('aria-hidden') !== 'true');

  // Shopify adds its cookie banner to <body> after load and hides it with an inline display: none
  const bannerShown = (el) => el.style.display !== 'none';
  const banner = document.getElementById('shopify-pc__banner');
  if (banner) {
    mirror(root, 'banner', banner, ['style'], bannerShown);
  } else {
    const waiter = new MutationObserver(() => {
      const added = document.getElementById('shopify-pc__banner');
      if (!added) return;
      waiter.disconnect();
      mirror(root, 'banner', added, ['style'], bannerShown);
    });
    waiter.observe(document.body, { childList: true });
    window.setTimeout(() => waiter.disconnect(), 15000);
  }
}

function buildDialog(root, template, { company, list, code, ready }) {
  root.append(template.content.cloneNode(true));
  template.remove();

  const dialog = root.querySelector('.exc-sms__dialog');
  const form = dialog.querySelector('.exc-sms__form');
  const input = dialog.querySelector('.exc-sms__input');
  const field = dialog.querySelector('.exc-sms__field');
  const error = dialog.querySelector('.exc-sms__error');
  const submit = form.querySelector('.exc-sms__submit');
  const steps = dialog.querySelectorAll('[data-exc-sms-step]');
  const heading = dialog.querySelector('.exc-sms__heading');
  let signedUp = false;
  let shoppingFor = '';

  const showError = (message) => {
    error.textContent = message;
    error.hidden = !message;
    field.toggleAttribute('data-invalid', Boolean(message));
    input.setAttribute('aria-invalid', message ? 'true' : 'false');
  };

  const showStep = (name) => {
    steps.forEach((step) => { step.hidden = step.dataset.excSmsStep !== name; });
  };

  // Screen 1: the answer picks the screen 2 form
  dialog.querySelectorAll('[data-exc-sms-choice]').forEach((button) => {
    button.addEventListener('click', () => {
      shoppingFor = button.dataset.excSmsChoice;
      showStep('form');
      input.focus();
    });
  });

  // Close button, "No thanks", "Keep shopping", and a tap on the dimmed area around the card
  dialog.querySelectorAll('[data-exc-sms-close]').forEach((button) => {
    button.addEventListener('click', () => dialog.close());
  });
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
  dialog.addEventListener('close', () => {
    root.removeAttribute('data-open');
    document.documentElement.classList.remove('exc-sms-lock');
    if (signedUp) root.hidden = true;
  });

  input.addEventListener('input', () => {
    if (!error.hidden) showError('');
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (submit.getAttribute('aria-busy') === 'true') return;

    const phone = toE164(input.value);
    if (!phone) {
      showError('Enter a 10 digit US or Canada mobile number.');
      input.focus();
      return;
    }
    if (!ready) {
      showError('Not connected yet: add the Klaviyo list id and the code in Customize > Pop-ups > EXC text signup.');
      return;
    }

    showError('');
    submit.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(company)}`, {
        method: 'POST',
        headers: {
          'content-type': 'application/vnd.api+json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'EXC site tab',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    phone_number: phone,
                    properties: shoppingFor ? { 'Shopping for': shoppingFor } : {},
                    subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } },
                  },
                },
              },
            },
            relationships: { list: { data: { type: 'list', id: list } } },
          },
        }),
      });
      if (!response.ok) throw new Error(`Klaviyo ${response.status}`);
    } catch (err) {
      submit.removeAttribute('aria-busy');
      showError('That did not go through. Check the number and try again.');
      return;
    }

    signedUp = true;
    store.set(DONE_KEY, '1');
    submit.removeAttribute('aria-busy');

    dialog.querySelector('[data-exc-sms-code]').textContent = code;
    showStep('done');
    heading.focus();

    // Shopify keeps a code visited at /discount/CODE for this visitor's checkout
    try {
      const applied = await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' });
      if (applied.ok) dialog.querySelector('[data-exc-sms-applied]').hidden = false;
    } catch (e) { /* the code is still on screen */ }
  });

  const copyLabel = dialog.querySelector('[data-exc-sms-copy-label]');
  dialog.querySelector('[data-exc-sms-copy]').addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(code);
      copyLabel.textContent = 'Copied';
    } catch (e) {
      copyLabel.textContent = code;
    }
    window.setTimeout(() => { copyLabel.textContent = 'Copy'; }, 2000);
  });

  return {
    open() {
      if (!signedUp) {
        showStep('choose');
        showError('');
      }
      root.setAttribute('data-open', '');
      document.documentElement.classList.add('exc-sms-lock');
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    },
  };
}

function init(root) {
  const { company, list, code } = root.dataset;
  const role = window.Shopify && window.Shopify.theme ? window.Shopify.theme.role : 'main';
  const ready = Boolean(company && list && code);

  // On the live theme it stays hidden until the list and code are set in the editor
  if (!ready && role === 'main') return;
  if (store.get(DONE_KEY) === '1') return;

  const template = root.querySelector('template[data-exc-sms-dialog]');
  if (!template) return;

  root.hidden = false;
  watchPage(root);

  let popup = null;
  root.querySelector('.exc-sms__tab').addEventListener('click', () => {
    popup = popup || buildDialog(root, template, { company, list, code, ready });
    popup.open();
  });
}

const root = document.querySelector('[data-exc-sms]');
if (root) init(root);
